import type { LogsEvent } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';
import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';

type BaseConfig = {
  applicationId: string;
  clientToken: string;
  env: string;
  service: string;
  privacyLevel?: RumInitConfiguration['defaultPrivacyLevel'];
  trackViewsManually?: boolean;
};

const { NODE_ENV } = process.env;
const VERCEL_ENV = process.env.NEXT_PUBLIC_VERCEL_ENV;
const GIT_COMMIT_SHA = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA;
const IS_DDOG_ENABLED = process.env.NEXT_PUBLIC_IS_DDOG_ENABLED === 'true';
const rumPercentage = Number(process.env.NEXT_PUBLIC_DDOG_RUM_PERCENTAGE);
const DDOG_RUM_PERCENTAGE =
  Number.isNaN(rumPercentage) || rumPercentage < 0 || rumPercentage > 100 ? 100 : rumPercentage;

/**
 * For each application, we need to set the service name and application ID in the Datadog configuration.
 * 1. Include the applicationId in an ENV with a NEXT_PUBLIC_ prefix.
 * 2. Include ENV in turbo.json
 * 3. Include in the list below
 */
type AppConfig = {
  service: string;
  id?: string;
  token?: string;
  /** The dashboard overrides this to 'mask': its replays show tenant PII the IDV flows never render. */
  privacyLevel?: RumInitConfiguration['defaultPrivacyLevel'];
  /** idv2 routes in memory, so the URL never changes; `Logger.setView` names each view instead. */
  trackViewsManually?: boolean;
};

const appServiceMap: Record<string, AppConfig> = {
  auth: {
    service: 'auth',
    id: process.env.NEXT_PUBLIC_DDOG_RUM_APPLICATION_AUTH,
    token: process.env.NEXT_PUBLIC_DDOG_CLIENT_TOKEN_AUTH,
  },
  dashboard: {
    service: 'dashboard',
    id: process.env.NEXT_PUBLIC_DDOG_RUM_APPLICATION_DASHBOARD,
    token: process.env.NEXT_PUBLIC_DDOG_CLIENT_TOKEN_DASHBOARD,
    privacyLevel: 'mask',
  },
  bifrost: {
    service: 'bifrost',
    id: process.env.NEXT_PUBLIC_DDOG_RUM_APPLICATION_BIFROST,
    token: process.env.NEXT_PUBLIC_DDOG_CLIENT_TOKEN_BIFROST,
    trackViewsManually: true,
  },
  handoff: {
    service: 'handoff',
    id: process.env.NEXT_PUBLIC_DDOG_RUM_APPLICATION_HANDOFF,
    token: process.env.NEXT_PUBLIC_DDOG_CLIENT_TOKEN_HANDOFF,
    trackViewsManually: true,
  },
  hosted: {
    service: 'hosted',
    id: process.env.NEXT_PUBLIC_DDOG_RUM_APPLICATION_HOSTED,
    token: process.env.NEXT_PUBLIC_DDOG_CLIENT_TOKEN_HOSTED,
    trackViewsManually: true,
  },
};

/**
 * Entries are matched with `includes`, so keep each one long enough that it cannot swallow an
 * unrelated error. Everything here reaches us through `forwardErrorsToLogs`, which ships every
 * `console.error` in the bundle — third-party libraries included.
 */
const hasMessageToOmit = (log?: string) =>
  Boolean(log) &&
  [
    '[object ProgressEvent]',
    'A component is changing a controlled',
    'A component is changing an uncontrolled',
    'Google Maps Places API library must be loaded',
    'If you want to write it to the DOM, pass a string instead',
    // Hydration mismatches, per FP-7692. Listed by code rather than as `Minified React error #`:
    // that swallowed the whole class, including #185 (infinite render loop) and #310 (hook order).
    // https://linear.app/footprint/issue/FP-7692/error-minified-react-error-418-423-425
    'Minified React error #418',
    'Minified React error #423',
    'Minified React error #425',
    'React does not recognize',
    'ResizeObserver loop',
    'Object Not Found Matching Id:', // Datadog SDK noise
    "null is not an object (evaluating 'o.id')", // Datadog SDK noise
    'Uncaught "Script error."', // Cross-origin script failure, stripped of everything actionable
    // The vendored MediaPipe WASM console.errors these once per camera mount on GPUs that can't
    // run its WebGL pipeline
    'Failed to link vertex and fragment shaders',
    'Failed to compile fragment shader',
    'Unable to create WebGLTexture',
    'Box.constructor - expected box to be', // face-api, on a frame it can't measure
    'The provided Place ID is no longer valid', // Google Places; nothing we can act on
    "reading 'M_ID'", // Google Translate rewriting the DOM underneath React
    'zaloJSV2', // Zalo's in-app browser injects a script that then fails
    // Browser extension errors
    'browser.storage.local', // Firefox extension API
    'chrome.storage', // Chrome extension API
    'chrome.runtime', // Chrome extension API
    'runtime.sendMessage', // Extension messaging; `chrome.runtime` above does not match this form
    'Extension context invalidated', // Extension lifecycle errors
  ].some(str => log?.includes(str));

const hasUrlToOmit = (log?: string) =>
  Boolean(log) &&
  [
    'ingest-lr.com',
    'maps.googleapis.com',
    'neuro-id.com',
    'neuroid.cloud',
    // Fraud-signal vendors, which only ever surface here as failed background requests
    'fp.risk.onefootprint.com',
    'api.fpjs.io',
    // Browser extension URLs
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
    // Third-party CSP violations
    'use.typekit.net',
  ].some(str => log?.includes(str));

/**
 * `forwardErrorsToLogs` also reports uncaught errors from code we do not control: Google Maps, and
 * whatever an in-app browser injects. Their messages are Closure-minified to tokens like `pa`, too
 * short for a `hasMessageToOmit` entry that would not also swallow real errors, so the stack is the
 * only thing left that separates them.
 */
const OWN_CODE_IN_STACK = ['/_next/static/', 'docs.opencv.org'];

const hasOwnCodeInStack = (stack?: string) => OWN_CODE_IN_STACK.some(marker => stack?.includes(marker));

const CSP_VIOLATION_URL = /^csp_violation: '(https?:\/\/[^']+)'/;

/**
 * A CSP violation is only ours to fix when the blocked resource is ours. Tenants embed the flow in
 * their own pages, so their fonts and images — and anything Google Translate injects — report
 * against our policy without telling us anything. Violations without a remote URL (`inline`,
 * `eval`) are kept: those would be our own bug.
 */
const isThirdPartyCspViolation = (message?: string) => {
  const blocked = message?.match(CSP_VIOLATION_URL)?.[1];
  if (!blocked) {
    return false;
  }

  try {
    const { hostname } = new URL(blocked);
    return hostname !== 'onefootprint.com' && !hostname.endsWith('.onefootprint.com');
  } catch {
    return false;
  }
};

const shouldSendLog = (log: LogsEvent): boolean => {
  if (log.http && log.http.status_code === 200) {
    return false;
  }

  // Status 0 means the request never completed, which in practice is React Query aborting in-flight
  // requests on navigation rather than a failure.
  if (log.origin === 'network' && log.http?.status_code === 0) {
    return false;
  }

  if (hasMessageToOmit(log.message) || hasMessageToOmit(log.error?.message)) {
    return false;
  }

  if (hasUrlToOmit(log.http?.url)) {
    return false;
  }

  if (hasUrlToOmit(log.error?.stack)) {
    return false;
  }

  if (log.origin === 'source' && !hasOwnCodeInStack(log.error?.stack)) {
    return false;
  }

  return true;
};

const shouldSendRumEvent = (ev: RumEvent): boolean => {
  if (ev.type !== 'error') {
    return true;
  }

  if (hasMessageToOmit(ev.error?.message) || isThirdPartyCspViolation(ev.error?.message)) {
    return false;
  }

  if (ev.error?.source === 'source' && !hasOwnCodeInStack(ev.error?.stack)) {
    return false;
  }

  return !hasUrlToOmit(ev.error?.stack);
};

const getDataDogConfig = (appName: string): undefined | BaseConfig => {
  const app = appServiceMap[appName];
  const isTest = NODE_ENV === 'test';
  const baseError = 'Datadog not initialized';

  if (isTest || !IS_DDOG_ENABLED) {
    console.warn(baseError);
    return;
  }
  if (!app?.service || !app.id || !app.token) {
    console.warn(`${baseError}: ${appName} is not configured`);
    return;
  }
  if (!VERCEL_ENV) {
    console.warn(`${baseError}: VERCEL_ENV is not defined`);
    return;
  }

  return {
    applicationId: app.id,
    clientToken: app.token,
    env: VERCEL_ENV,
    service: app.service,
    privacyLevel: app.privacyLevel,
    trackViewsManually: app.trackViewsManually,
  };
};

const hasManualRumViews = (appName: string): boolean => Boolean(appServiceMap[appName]?.trackViewsManually);

const initDataDogLogs = (appName: string): boolean => {
  const config = getDataDogConfig(appName);
  if (!config) {
    return false;
  }

  // https://docs.datadoghq.com/logs/log_collection/javascript/#configuration
  datadogLogs.init({
    clientToken: config.clientToken,
    env: config.env,
    service: config.service,
    site: 'datadoghq.com', // `site` refers to the Datadog site parameter of your organization. see https://docs.datadoghq.com/getting_started/site/
    version: `${config.service}-${GIT_COMMIT_SHA}`, // Specify a version number to identify the deployed version of your application in Datadog.

    allowFallbackToLocalStorage: true, // If usePartitionedCrossSiteSessionCookie is true, then useLocalStorage is ignored.
    forwardErrorsToLogs: true, // Set to false to stop forwarding console.error logs, uncaught exceptions and network errors to Datadog.
    sessionSampleRate: 100, // The percentage of sessions to track: 100 for all, 0 for none. Only tracked sessions send logs.
    storeContextsAcrossPages: true, // Store global context and user context in localStorage to preserve them along the user navigation.
    trackSessionAcrossSubdomains: true, // Track sessions across subdomains.
    usePartitionedCrossSiteSessionCookie: true, // Store session cookies in partitioned cross-site cookies.

    beforeSend: beforeSendLog,
  });

  return true;
};

// OAuth providers redirect back with a single-use code and the flow's state token in the query,
// and the App Clip handoff URL carries the onboarding session token in authToken.
const SECRET_QUERY_PARAMS = /([?&](?:code|state|authToken)=)[^&#]*/g;

const redactUrl = (url: string) => url.replace(/#.*/, '#fragment').replace(SECRET_QUERY_PARAMS, '$1redacted');

// The SDKs pass the onboarding session token in the page's fragment (#utok_…, #obtok_…), and the
// referrer and the page's own resource entry can carry it too.
const beforeSendLog = (log: LogsEvent): boolean => {
  log.view.url = redactUrl(log.view.url);
  if (log.view.referrer) {
    log.view.referrer = redactUrl(log.view.referrer);
  }
  return shouldSendLog(log);
};

const beforeSendRum = (ev: RumEvent): boolean => {
  ev.view.url = redactUrl(ev.view.url);
  if (ev.view.referrer) {
    ev.view.referrer = redactUrl(ev.view.referrer);
  }
  if (ev.type === 'resource') {
    ev.resource.url = redactUrl(ev.resource.url);
  }
  return shouldSendRumEvent(ev);
};

const initDataDogRum = (appName: string): boolean => {
  const config = getDataDogConfig(appName);
  if (!config) {
    return false;
  }

  const localBackendTracing: RumInitConfiguration['allowedTracingUrls'] =
    NODE_ENV === 'development' ? [{ match: 'http://localhost:8000', propagatorTypes: ['tracecontext'] }] : [];

  const { privacyLevel, ...baseConfig } = config;

  // https://docs.datadoghq.com/real_user_monitoring/browser/setup/#initialization-parameters
  datadogRum.init({
    ...baseConfig,
    applicationId: config.applicationId, // The RUM application ID.
    clientToken: config.clientToken, // Client tokens are unique to your organization.
    env: config.env,
    service: config.service,
    site: 'datadoghq.com', // `site` refers to the Datadog site parameter of your organization. see https://docs.datadoghq.com/getting_started/site/
    version: `${config.service}-${GIT_COMMIT_SHA}`, // Specify a version number to identify the deployed version of your application in Datadog

    allowFallbackToLocalStorage: true, // If usePartitionedCrossSiteSessionCookie is true, then useLocalStorage is ignored.
    defaultPrivacyLevel: privacyLevel ?? 'mask-user-input', // `mask`, `mask-user-input`, or `allow`
    sessionReplaySampleRate: DDOG_RUM_PERCENTAGE, // The percentage of tracked sessions with Browser RUM & Session Replay pricing features: 100 for all, 0
    sessionSampleRate: 100, // The percentage of sessions to track: 100 for all, 0 for none.
    startSessionReplayRecordingManually: true, // Manually start the Session Replay
    storeContextsAcrossPages: true, // Store global context and user context in localStorage to preserve them along the user navigation.
    trackLongTasks: true, // Enables collection of long task events.
    trackResources: true, // Enables collection of resource events.
    trackSessionAcrossSubdomains: true, // Track session across subdomains
    trackUserInteractions: true, // The trackUserInteractions parameter enables the automatic collection of user clicks in your application. Sensitive and private data contained in your pages may be included to identify the elements interacted with.
    usePartitionedCrossSiteSessionCookie: true, // Use partitioned cross-site session cookies.
    allowedTracingUrls: [
      { match: 'https://api.onefootprint.com', propagatorTypes: ['tracecontext'] },
      { match: 'https://api.dev.onefootprint.com', propagatorTypes: ['tracecontext'] },
      ...localBackendTracing,
    ],
    beforeSend: beforeSendRum,
  });

  return true;
};

const dataDogErrorEvent = (err: Error, msg: string, msgContext?: object) =>
  datadogLogs.logger.error(msg, msgContext ? { context: msgContext } : undefined, err);

const dataDogTrackEvent = (msg: string, msgContext?: object, err?: Error) =>
  datadogLogs.logger.debug(msg, msgContext ? { context: msgContext } : undefined, err);

const dataDogWarnEvent = (msg: string, msgContext?: object, err?: unknown) => {
  datadogLogs.logger.warn(
    msg,
    msgContext ? { context: msgContext } : undefined,
    err instanceof Error ? err : undefined,
  );
};

const dataDogInfoEvent = (msg: string, msgContext?: object, err?: unknown) =>
  datadogLogs.logger.info(
    msg,
    msgContext ? { context: msgContext } : undefined,
    err instanceof Error ? err : undefined,
  );

const dataDogAction = (act: string, actContext?: object) => {
  try {
    const rumGlobal = datadogRum.getGlobalContext();
    const logGlobal = datadogLogs.getGlobalContext();
    return datadogRum.addAction(act, { ...rumGlobal, ...logGlobal, context: actContext });
  } catch (_e) {
    return undefined;
  }
};

export {
  beforeSendLog,
  beforeSendRum,
  dataDogAction,
  dataDogErrorEvent,
  dataDogInfoEvent,
  dataDogTrackEvent,
  dataDogWarnEvent,
  hasManualRumViews,
  hasMessageToOmit,
  hasOwnCodeInStack,
  hasUrlToOmit,
  initDataDogLogs,
  initDataDogRum,
  isThirdPartyCspViolation,
  redactUrl,
  shouldSendLog,
  shouldSendRumEvent,
};
